<template>
  <el-dialog
    :visible="value"
    :title="title"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :before-close="handleClose"
    :show-close="showClose"
    append-to-body
    width="1020px"
  >
    <slot name="header" />
    <el-row type="flex" :gutter="20">
      <el-col>
        <video
          v-show="!isTakingPhoto"
          ref="videoRef"
          width="640"
          height="480"
          autoplay
          muted
        />
        <canvas
          v-show="isTakingPhoto"
          ref="canvasRef"
          width="640"
          height="480"
          style="width: 640px; height: 480px"
        />
        <div style="margin-top: 20px; text-align: center">
          <el-button
            v-show="!isTakingPhoto"
            type="primary"
            @click="handleTakePhoto"
          >
            拍照
          </el-button>
          <el-button
            v-show="isTakingPhoto"
            type="danger"
            @click="handleRetakePhoto"
          >
            重拍
          </el-button>
          <el-button
            v-show="isTakingPhoto"
            type="success"
            @click="handleSavePhoto"
          >
            完成
          </el-button>
        </div>
      </el-col>
      <el-col>
        <el-upload
          ref="upload"
          :file-list="fileList"
          :action="action"
          :data="uploadData"
          :limit="limit"
          :before-upload="handleBeforeUpload"
          :on-exceed="handleExceed"
          :on-change="handleChange"
          :on-remove="handleRemove"
          :on-success="handleSuccess"
          :on-error="handleError"
          :accept="accept"
          list-type="picture-card"
          multiple
          :with-credentials="
            !accept.includes('jpg') &&
            !accept.includes('mp4') &&
            action.indexOf('yishou-img') === -1
          "
        >
          <i slot="default" class="el-icon-plus" />
          <div slot="file" slot-scope="{ file }" class="image-view">
            <img
              class="el-upload-list__item-thumbnail"
              :src="file.url"
              alt=""
            />
            <span class="el-upload-list__item-actions">
              <span
                class="el-upload-list__item-preview"
                @click="handlePreview(file)"
              >
                <i class="el-icon-zoom-in" />
              </span>
              <span
                class="el-upload-list__item-delete"
                @click="handleRemove(file)"
              >
                <i class="el-icon-delete" />
              </span>
            </span>
          </div>
        </el-upload>
      </el-col>
    </el-row>
    <div slot="footer" style="text-align: right">
      <el-button v-if="isShowCloseBtn" @click="handleClose"> 关闭 </el-button>
      <el-button
        :disabled="isDisabled"
        :loading="isUploading"
        type="primary"
        @click="handleConfirm"
      >
        保存
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import * as dayjs from "dayjs";
import { mapGetters } from "vuex";
import md5 from "js-md5";
import { uploadFileBlob } from "@/tool/upload";
import { Camera } from "@/tool/helper";

const CameraType = {
  limitType: 1, // 照片数量达到限制后，不能再拍照
  replaceType: 2, // 照片数量达到限制后，再拍照，新照片替换最后一张
};
const parser = new DOMParser();
export default {
  name: "YsCamera",
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    /**
     * 图片列表
     */
    list: {
      type: Array,
      default: () => [],
    },
    /**
     * 图片数量限制，默认为 0 不限制
     */
    limit: {
      type: Number,
      default: 0,
    },
    /**
     * 图片是否可供预览
     */
    preview: {
      type: Boolean,
      default: false,
    },
    /**
     * 图片是否可本地上传
     */
    uploadLocal: {
      type: Boolean,
      default: false,
    },
    /**
     * 弹窗标题
     */
    title: {
      type: String,
      default: "拍照",
    },
    /**
     * 是否显示关闭按钮X
     */
    showClose: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否底部关闭按钮
     */
    isShowCloseBtn: {
      type: Boolean,
      default: true,
    },
    /**
     * 拍照类型
     */
    cameraType: {
      type: Number,
      default: CameraType.limitType,
    },
  },
  data() {
    return {
      camera: null,
      canvasContext: null,
      accept: ["jpg", "jpeg", "png", "gif", "svg"].join(","),
      uploadData: {},
      action: "",
      isTakingPhoto: false,
      fileList: [],
    };
  },
  computed: {
    ...mapGetters({
      obsUploadData: "obsUploadData",
    }),
    isUploading() {
      return this.fileList.some((item) => item.status === "uploading");
    },
    isDisabled() {
      return (
        this.fileList.length <= 0 && this.cameraType === CameraType.replaceType
      );
    },
  },
  watch: {
    value: {
      handler(val) {
        if (val) {
          this.handleInit();
          this.handleInitObsData();
        }
      },
      immediate: true,
    },
    list: {
      handler(list) {
        this.$refs.upload && this.$refs.upload.clearFiles();
        this.fileList = list;
      },
    },
  },
  mounted() {
    const { bucket_name, endpoint, uptoken } = this.obsUploadData;
    const { access_key_id, domain, policy, signature, success_action_status } =
      uptoken || {};
    this.uploadData = {
      AccessKeyId: access_key_id,
      domain,
      policy,
      signature,
      success_action_status,
    };
    this.action = `https://${bucket_name}.${endpoint}/`;
  },
  methods: {
    handleInit() {
      this.$nextTick(() => {
        try {
          this.camera = new Camera();
          this.camera.startVideo(this.$refs.videoRef);
        } catch (e) {
          this.$message.error(e);
        }
      });
    },
    handleInitObsData() {
      const { bucket_name, endpoint, uptoken } = this.obsUploadData;
      const {
        access_key_id,
        domain,
        policy,
        signature,
        success_action_status,
      } = uptoken || {};
      this.uploadData = {
        AccessKeyId: access_key_id,
        domain,
        policy,
        signature,
        success_action_status,
      };
      this.action = `https://${bucket_name}.${endpoint}/`;
    },
    handleStopCamera() {
      this.camera && this.camera.stopMedia();
      this.camera = null;
    },
    handleBeforeUpload(file) {
      this.uploadData.key = `obs/erp/upload/${dayjs().format(
        "YYYY/MM/DD"
      )}/${md5(file.name + Date.now())}.${file.name
        .toLowerCase()
        .split(".")
        .splice(-1)}`;
    },
    handleExceed(files, fileList) {
      this.$message.info(
        `最多上传 ${this.limit} 个文件，本次选择了 ${files.length} 个文件，已有 ${fileList.length} 个文件`
      );
    },
    handleChange(file, fileList) {
      this.fileList = fileList;
    },
    handleRemove(file) {
      const index = this.fileList.findIndex((item) => item.name === file.name);
      this.fileList.splice(index, 1);
    },
    handleSuccess(response, file, fileList) {
      this.fileList = fileList.map((item) => {
        if (item.response) {
          const xmlDoc = parser.parseFromString(item.response, "text/xml");
          item.name = xmlDoc.querySelector("Key").innerHTML;
          item.url = `${this.action}${item.name}`;
        }
        return item;
      });
    },
    handleError(err, file, fileList) {
      this.$message.warning("图片上传失败，请重新上传");
      this.fileList = fileList.filter((item) => item.status !== "fail");
    },
    handleExceedLimit() {
      if (this.limit && this.fileList.length >= this.limit) {
        this.$message.info(`最多上传 ${this.limit} 个文件`);
        return true;
      }
    },
    handleTakePhoto() {
      if (this.cameraType === CameraType.limitType) {
        const isExceed = this.handleExceedLimit();
        if (isExceed) {
          return false;
        }
      }
      this.canvasContext = this.$refs.canvasRef.getContext("2d");
      this.canvasContext.drawImage(this.$refs.videoRef, 0, 0, 640, 480);
      this.isTakingPhoto = true;
    },
    handleRetakePhoto() {
      this.isTakingPhoto = false;
    },
    async handleSavePhoto() {
      if (!this.camera) {
        this.$message.error("未捕获到摄像头");
        return;
      }
      try {
        const canvas = this.$refs.canvasRef;
        const blob = await new Promise((resolve) => {
          canvas.toBlob((blob) => {
            resolve(blob);
          }, "image/png");
        });
        const res = await uploadFileBlob({ target: blob });
        if (this.cameraType === CameraType.replaceType) {
          if (this.fileList.length >= this.limit) {
            this.fileList.splice(this.fileList.length - 1, 1, res);
          } else {
            this.fileList.push(res);
          }
        } else {
          this.fileList.push(res);
        }
      } catch (e) {
        this.$noop();
      }
      this.isTakingPhoto = false;
    },
    handlePreview(file) {
      const index = this.fileList.findIndex((item) => item.name === file.name);
      this.$viewerApi({
        images: this.fileList.map((item) => item.url),
        options: {
          initialViewIndex: index,
        },
      });
    },
    handleClose() {
      this.$emit("input", false);
      this.isTakingPhoto = false;
      this.handleStopCamera();
      this.fileList = []; // 清空fileList数组
    },
    handleConfirm() {
      const fileList = this.fileList.map((item) => {
        return {
          name: item.name,
          url: item.url,
        };
      });
      this.$emit("complete", fileList);
      this.handleClose();
    },
  },
};
</script>
<style scoped>
.image-view {
  position: relative;
}

.img {
  width: 100%;
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/YsComponents/YsCamera.vue